/**
 * Task #429 — Collaborator / contact role catalog manager.
 *
 * Three-tier catalog mirroring the talent-types pattern, rendered as ONE
 * reusable component driven by a `surface` prop:
 *
 *   - "platform"  — super admin (Platform Workspace). CRUD platform defaults.
 *                   Endpoints: /api/platform/collaborator-roles
 *   - "workspace" — workspace admin. Hide platform defaults + CRUD workspace
 *                   custom roles. Endpoints: /api/collaborator-roles/workspace*
 *   - "talent"    — talent portal manager (per act). Hide inherited
 *                   (platform + workspace) roles + CRUD talent custom roles.
 *                   Endpoints: /api/collaborator-roles/talent/:talentId*
 *
 * The workspace/talent surfaces read the effective catalog from
 * GET /api/collaborator-roles[?talentId]; the platform surface reads the raw
 * platform list from GET /api/platform/collaborator-roles.
 */
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Check, X, Loader2, Pencil, ChevronUp, ChevronDown, Users } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/hooks/use-workspace";

interface RoleRow {
  id: string;
  name: string;
  sortOrder: number;
  isActive?: boolean;
}

interface RoleCatalog {
  platformRoles: RoleRow[];
  workspaceRoles: RoleRow[];
  hiddenPlatformRoleIds: string[];
  talentRoles: RoleRow[];
  talentHides: { roleId: string; roleSource: string }[];
}

type Surface = "platform" | "workspace" | "talent";

interface Props {
  surface: Surface;
  talentId?: string;
  inPanel?: boolean;
}

export function CollaboratorRolesManager({ surface, talentId, inPanel = false }: Props) {
  const { toast } = useToast();
  const { buildPath } = useWorkspace();
  const [newName, setNewName] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  // ── Platform surface (flat list) ─────────────────────────────────────
  const platformQuery = useQuery<RoleRow[]>({
    queryKey: ["/api/platform/collaborator-roles"],
    queryFn: async () => {
      const r = await fetch("/api/platform/collaborator-roles", { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load");
      return r.json();
    },
    enabled: surface === "platform",
  });

  // ── Workspace + talent surfaces (effective catalog) ──────────────────
  const catalogKey = surface === "talent" ? ["/api/collaborator-roles", talentId] : ["/api/collaborator-roles"];
  const catalogQuery = useQuery<RoleCatalog>({
    queryKey: catalogKey,
    queryFn: async () => {
      const url = surface === "talent" ? `/api/collaborator-roles?talentId=${talentId}` : "/api/collaborator-roles";
      const r = await fetch(buildPath(url), { credentials: "include" });
      if (!r.ok) throw new Error("Failed to load");
      return r.json();
    },
    enabled: surface !== "platform" && (surface !== "talent" || !!talentId),
  });

  const invalidate = () => {
    if (surface === "platform") {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/collaborator-roles"] });
    } else {
      queryClient.invalidateQueries({ queryKey: ["/api/collaborator-roles"] });
    }
  };

  const isLoading = surface === "platform" ? platformQuery.isLoading : catalogQuery.isLoading;
  const catalog = catalogQuery.data;

  // Inherited roles (for talent surface): platform (minus ws hides) + workspace.
  const inheritedRoles: { id: string; name: string; source: "platform" | "workspace" }[] =
    surface === "talent" && catalog
      ? [
          ...catalog.platformRoles
            .filter((p) => !catalog.hiddenPlatformRoleIds.includes(p.id))
            .map((p) => ({ id: p.id, name: p.name, source: "platform" as const })),
          ...catalog.workspaceRoles.map((w) => ({ id: w.id, name: w.name, source: "workspace" as const })),
        ]
      : [];

  const talentHideSet = new Set((catalog?.talentHides ?? []).map((h) => `${h.roleSource}:${h.roleId}`));
  const hiddenPlatformIds = new Set(catalog?.hiddenPlatformRoleIds ?? []);

  // ── Mutations: ADD custom ────────────────────────────────────────────
  const addMutation = useMutation({
    mutationFn: async (name: string) => {
      if (surface === "platform") {
        return (await apiRequest("POST", "/api/platform/collaborator-roles", { name })).json();
      }
      if (surface === "workspace") {
        return (await apiRequest("POST", buildPath("/api/collaborator-roles/workspace"), { name })).json();
      }
      return (await apiRequest("POST", buildPath(`/api/collaborator-roles/talent/${talentId}`), { name })).json();
    },
    onSuccess: () => {
      invalidate();
      setNewName("");
      setIsAdding(false);
      toast({ title: "Role added" });
    },
    onError: (err: any) => toast({ title: err?.message || "Failed to add role", variant: "destructive" }),
  });

  // ── Mutations: RENAME custom ─────────────────────────────────────────
  const renameMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      if (surface === "platform") {
        return apiRequest("PATCH", `/api/platform/collaborator-roles/${id}`, { name });
      }
      if (surface === "workspace") {
        return apiRequest("PATCH", buildPath(`/api/collaborator-roles/workspace/${id}`), { name });
      }
      return apiRequest("PATCH", buildPath(`/api/collaborator-roles/talent/${talentId}/${id}`), { name });
    },
    onSuccess: () => {
      invalidate();
      setEditingId(null);
      setEditName("");
      toast({ title: "Role renamed" });
    },
    onError: (err: any) => toast({ title: err?.message || "Failed to rename", variant: "destructive" }),
  });

  // ── Mutations: DELETE custom ─────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (surface === "platform") {
        return apiRequest("DELETE", `/api/platform/collaborator-roles/${id}`);
      }
      if (surface === "workspace") {
        return apiRequest("DELETE", buildPath(`/api/collaborator-roles/workspace/${id}`));
      }
      return apiRequest("DELETE", buildPath(`/api/collaborator-roles/talent/${talentId}/${id}`));
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Role removed" });
    },
    onError: (err: any) => toast({ title: err?.message || "Failed to remove", variant: "destructive" }),
  });

  // ── Mutations: REORDER custom ────────────────────────────────────────
  const reorderMutation = useMutation({
    mutationFn: async (swaps: { id: string; sortOrder: number }[]) => {
      const path = (id: string) =>
        surface === "platform"
          ? `/api/platform/collaborator-roles/${id}`
          : surface === "workspace"
          ? buildPath(`/api/collaborator-roles/workspace/${id}`)
          : buildPath(`/api/collaborator-roles/talent/${talentId}/${id}`);
      await Promise.all(swaps.map((s) => apiRequest("PATCH", path(s.id), { sortOrder: s.sortOrder })));
    },
    onSuccess: () => invalidate(),
    onError: (err: any) => toast({ title: err?.message || "Failed to reorder", variant: "destructive" }),
  });

  // ── Mutations: HIDE / UNHIDE inherited ───────────────────────────────
  const toggleHideMutation = useMutation({
    mutationFn: async (args: { roleId: string; roleSource: "platform" | "workspace"; hide: boolean }) => {
      if (surface === "workspace") {
        // Workspace can only hide platform defaults.
        if (args.hide) {
          return apiRequest("POST", buildPath("/api/collaborator-roles/workspace/hide"), {
            platformCollaboratorRoleId: args.roleId,
          });
        }
        return apiRequest("DELETE", buildPath(`/api/collaborator-roles/workspace/hide/${args.roleId}`));
      }
      // Talent can hide platform OR workspace roles.
      if (args.hide) {
        return apiRequest("POST", buildPath(`/api/collaborator-roles/talent/${talentId}/hide`), {
          roleId: args.roleId,
          roleSource: args.roleSource,
        });
      }
      return apiRequest(
        "DELETE",
        buildPath(`/api/collaborator-roles/talent/${talentId}/hide/${args.roleSource}/${args.roleId}`),
      );
    },
    onSuccess: () => invalidate(),
    onError: (err: any) => toast({ title: err?.message || "Failed to update visibility", variant: "destructive" }),
  });

  // The editable custom list for the active surface.
  const customRoles: RoleRow[] =
    surface === "platform"
      ? platformQuery.data ?? []
      : surface === "workspace"
      ? catalog?.workspaceRoles ?? []
      : catalog?.talentRoles ?? [];

  const moveCustom = (index: number, dir: -1 | 1) => {
    const a = customRoles[index];
    const b = customRoles[index + dir];
    if (!a || !b) return;
    reorderMutation.mutate([
      { id: a.id, sortOrder: b.sortOrder },
      { id: b.id, sortOrder: a.sortOrder },
    ]);
  };

  const customLabel = surface === "platform" ? "Platform Roles" : "Custom Roles";
  const customHint =
    surface === "platform"
      ? "Default collaborator/contact roles available to every workspace."
      : surface === "workspace"
      ? "Roles available only in this workspace. They never affect other workspaces."
      : "Roles available only for this act, on top of inherited workspace roles.";

  const renderCustomList = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium">{customLabel}</h4>
          <p className="text-xs text-muted-foreground mt-0.5">{customHint}</p>
        </div>
        {!isAdding && (
          <Button size="sm" variant="outline" onClick={() => setIsAdding(true)} data-testid="button-add-collab-role">
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Add Role
          </Button>
        )}
      </div>

      {isAdding && (
        <div className="flex items-center gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newName.trim()) addMutation.mutate(newName.trim());
              if (e.key === "Escape") {
                setIsAdding(false);
                setNewName("");
              }
            }}
            placeholder="e.g. Sound Tech"
            autoFocus
            className="h-8 text-sm flex-1"
            data-testid="input-new-collab-role"
          />
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() => newName.trim() && addMutation.mutate(newName.trim())}
            disabled={!newName.trim() || addMutation.isPending}
            data-testid="button-confirm-add-collab-role"
          >
            {addMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 text-green-600" />}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() => {
              setIsAdding(false);
              setNewName("");
            }}
            data-testid="button-cancel-add-collab-role"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      {customRoles.length > 0 ? (
        <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
          {customRoles.map((role, index) => {
            const isEditing = editingId === role.id;
            return (
              <div
                key={role.id}
                className="flex items-center justify-between gap-2 px-3 py-2.5 bg-card group"
                data-testid={`row-collab-role-${role.id}`}
              >
                {isEditing ? (
                  <>
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && editName.trim()) renameMutation.mutate({ id: role.id, name: editName.trim() });
                        if (e.key === "Escape") {
                          setEditingId(null);
                          setEditName("");
                        }
                      }}
                      autoFocus
                      className="h-8 text-sm flex-1"
                      data-testid={`input-edit-collab-role-${role.id}`}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => editName.trim() && renameMutation.mutate({ id: role.id, name: editName.trim() })}
                      disabled={!editName.trim() || renameMutation.isPending}
                      data-testid={`button-confirm-edit-collab-role-${role.id}`}
                    >
                      {renameMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 text-green-600" />}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => {
                        setEditingId(null);
                        setEditName("");
                      }}
                      data-testid={`button-cancel-edit-collab-role-${role.id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm truncate">{role.name}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => moveCustom(index, -1)}
                        disabled={index === 0 || reorderMutation.isPending}
                        data-testid={`button-move-up-collab-role-${role.id}`}
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => moveCustom(index, 1)}
                        disabled={index === customRoles.length - 1 || reorderMutation.isPending}
                        data-testid={`button-move-down-collab-role-${role.id}`}
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => {
                          setEditingId(role.id);
                          setEditName(role.name);
                        }}
                        data-testid={`button-edit-collab-role-${role.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                        onClick={() => deleteMutation.mutate(role.id)}
                        disabled={deleteMutation.isPending}
                        data-testid={`button-delete-collab-role-${role.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        !isAdding && <p className="text-sm text-muted-foreground py-2">No {surface === "platform" ? "roles" : "custom roles"} yet.</p>
      )}
    </div>
  );

  // Inherited section: workspace surface shows platform defaults (hide toggle);
  // talent surface shows platform + workspace (hide toggle).
  const renderInherited = () => {
    if (surface === "platform") return null;
    if (surface === "workspace") {
      const platformRoles = catalog?.platformRoles ?? [];
      return (
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-medium">Platform Defaults</h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              Toggle off roles you don't use. Existing assignments keep their value.
            </p>
          </div>
          <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
            {platformRoles.length === 0 ? (
              <div className="py-4 text-center text-sm text-muted-foreground">No platform defaults configured.</div>
            ) : (
              platformRoles.map((pt) => {
                const isHidden = hiddenPlatformIds.has(pt.id);
                return (
                  <div
                    key={pt.id}
                    className="flex items-center justify-between px-3 py-2.5 bg-card"
                    data-testid={`row-inherited-collab-role-${pt.id}`}
                  >
                    <span className={`text-sm ${isHidden ? "text-muted-foreground line-through" : ""}`}>{pt.name}</span>
                    <Switch
                      checked={!isHidden}
                      onCheckedChange={(checked) =>
                        toggleHideMutation.mutate({ roleId: pt.id, roleSource: "platform", hide: !checked })
                      }
                      disabled={toggleHideMutation.isPending}
                      data-testid={`switch-inherited-collab-role-${pt.id}`}
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>
      );
    }
    // talent surface
    return (
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-medium">Inherited Roles</h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            Platform and workspace roles. Toggle off any this act doesn't use.
          </p>
        </div>
        <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
          {inheritedRoles.length === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">No inherited roles.</div>
          ) : (
            inheritedRoles.map((r) => {
              const isHidden = talentHideSet.has(`${r.source}:${r.id}`);
              return (
                <div
                  key={`${r.source}:${r.id}`}
                  className="flex items-center justify-between px-3 py-2.5 bg-card"
                  data-testid={`row-inherited-collab-role-${r.id}`}
                >
                  <span className={`flex items-center gap-2 text-sm ${isHidden ? "text-muted-foreground line-through" : ""}`}>
                    {r.name}
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      {r.source === "platform" ? "Platform" : "Workspace"}
                    </Badge>
                  </span>
                  <Switch
                    checked={!isHidden}
                    onCheckedChange={(checked) => toggleHideMutation.mutate({ roleId: r.id, roleSource: r.source, hide: !checked })}
                    disabled={toggleHideMutation.isPending}
                    data-testid={`switch-inherited-collab-role-${r.id}`}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const body = (
    <div className="space-y-6">
      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {renderInherited()}
          {renderCustomList()}
        </>
      )}
    </div>
  );

  if (inPanel) {
    return (
      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-4 h-4" />
            <h3 className="font-semibold text-sm">Collaborator Roles</h3>
          </div>
          <p className="text-xs text-muted-foreground">
            Configure the roles available when adding people to a call sheet roster.
          </p>
        </div>
        {body}
      </div>
    );
  }

  return body;
}
